/**
 * The Genre Mixer's "song ready" result card.
 *
 * A CONFIRMATION AND TWO WAYS ONWARD — and, as of Batch 6 §7, NOT A PLAYER.
 *
 * It used to carry a full transport: an accent play/pause button, an 18-bar waveform and a
 * PlayerBar scrubber that appeared once the track was active. That was a third playback
 * surface for one song. Generating from the mixer hands the result to the composer and
 * navigates there (see routes/GenreMixer.generate), so Create's own result panel already
 * offers a transport for this exact song, and the global mini player offers another the
 * moment either is used. §7: "REMOVE the song playback card/player that appears inside the
 * Genre Mixer result area. The Genre Mixer section should not contain a duplicate music
 * playback card… The global player should remain available through the application's normal
 * player system."
 *
 * WHAT IS DELIBERATELY STILL HERE, because §7 also says "Do not remove the actual generated
 * song": the song exists, is named, is downloadable and opens in the Studio. Coming back to
 * /genre-mixer after the handover still finds its result — resultSongId is in the mixer
 * store — so the card remains the answer to "what did I just make". It simply no longer
 * plays it.
 *
 * Its hero gradient is one of the app's four artwork variants — 45% saturation,
 * 24%→12% lightness, hue×7 — but it is 90px tall with a bottom-left icon rather
 * than ArtworkPlaceholder's centred one, so it stays hand-rolled here. Unifying
 * them would move the icon, which is a visible change.
 *
 * SIMULATED. Download writes a text file of the song's details.
 */
import { CheckCircle2, Disc3, Download, Music } from 'lucide-react';
import { Btn } from '../ui';
import { joinWithMiddleDot } from '../../lib/formatting';
import { idHue } from '../../lib/hash';
import { effectiveSongLength } from '../../lib/songRuntime';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface MixerResultCardProps {
  song: Song;
  onDownload: () => void;
  onOpenStudio: () => void;
}

export default function MixerResultCard({
  song,
  onDownload,
  onOpenStudio,
}: MixerResultCardProps) {
  return (
    <div
      style={{
        background: c.surface1,
        border: `1px solid ${c.accentBorder}`,
        borderRadius: 12,
        padding: '16px 18px',
        marginBottom: 18,
      }}
    >
      <p
        style={{
          fontSize: 12,
          color: c.accent,
          margin: '0 0 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <CheckCircle2 size={13} />
        Song ready
      </p>
      <div
        style={{
          height: 90,
          borderRadius: 10,
          marginBottom: 12,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, hsl(${idHue(song.id)}, 45%, 24%), hsl(${idHue(song.id, 7)}, 45%, 12%))`,
        }}
      >
        <Music
          size={22}
          color="rgba(255,255,255,0.5)"
          style={{ position: 'absolute', bottom: 10, left: 12 }}
        />
      </div>
      <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 2px', fontFamily: 'Space Grotesk' }}>
        {song.title}
      </p>
      {/* The result's genre and duration — metadata, which theme.ts puts at text2. The
          duration is the song's own length rather than a position, because there is no
          playhead on this card to have a position. */}
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 12px' }}>
        {joinWithMiddleDot([song.genre, effectiveSongLength(song.length)])}
      </p>
      {/*
        Two actions, and neither of them plays anything.

        Where the transport used to be. "Open in Studio" is the one that answers "let me
        hear it": the workspace has a docked transport for the song it is open on, which is
        the app's normal player rather than a fourth copy of one. The composer this screen
        hands off to has the other.
      */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn variant="secondary" onClick={onDownload}>
          <Download size={13} />
          Download
        </Btn>
        {/* One navigation now, to the Studio's own URL. The prototype had to switch
            the view *and* open the drawer as two separate steps. */}
        <Btn onClick={onOpenStudio}>
          <Disc3 size={13} />
          Open in Studio
        </Btn>
      </div>
    </div>
  );
}
